import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DashInviteFormSource,
  EmailValidationResult,
  type IndividualInviteFormContact,
  InviteFormStatus,
  type InviteWholeTeamFormContact,
} from "../types";
import { isValidInvitee } from "../utils/isValidInvitee";
import { useInviteFormEmailValidation } from "./useEmailValidation";
import {
  INITIAL_INVITE_FORM_DATA_STATUS,
  type InitialInviteFormData,
  useInitialInviteFormData,
} from "./useInviteFormData";
import {
  type InviteFormSubmissionResult,
  SUBMISSION_STATUS_SUBMITTED,
  SUBMISSION_STATUS_SUBMITTING,
  useSubmitInviteForm,
} from "./useSubmitInviteForm";

import type { EmailValidator } from "./useEmailValidation";

const INITIAL_EMPTY_INPUT_COUNT_MODAL_WITH_TEAM_CHECKBOX = 1;

export type UseManagedInviteFormArgs = {
  initialMessage?: string;
  initialEmptyInputCount?: number;
  emailValidator?: EmailValidator;
};

interface UseManagedInviteFormReturnType {
  managedInviteFormProps: ManagedInviteFormProps;
}

export interface UseManagedInviteFormWithInitialDataArgs
  extends Omit<
    UseManagedInviteFormArgs,
    "emailValidator" | "initialEmptyInputCount"
  > {
  /**
   * The default number of empty input fields to show in the invite form. Note that this value
   *   maybe overriden by results from the initial data fetch to save space for other UI components,
   *   e.g. the invite-entire-team checkbox in the invite modal
   */
  defaultInitialEmptyInputCount?: number;
  dashInviteFormSource: DashInviteFormSource;
  onFormValidationChange?: (valid: boolean) => void;
}

export interface UseManagedInviteFormWithInitialDataReturnType {
  inviteFormStatus: InviteFormStatus;
  submissionResult: InviteFormSubmissionResult | null;
  handleSubmit: () => void;
  managedInviteFormProps: ManagedInviteFormPropsWithInitialData | null;
}

interface ManagedInviteFormProps {
  // States and calculated values
  individualContacts: Readonly<IndividualInviteFormContact[]>;
  teamContacts: Readonly<InviteWholeTeamFormContact[]>;
  validIndividualContactCount: number;
  validTeamContactCount: number;
  initialMessage: string;
  customMessage?: string;
  disabled?: boolean;
  // Handlers
  addEmptyIndividualContact: () => void;
  updateIndividualContact: (contact: IndividualInviteFormContact) => void;
  validateBatchContacts: (
    contacts: readonly IndividualInviteFormContact[],
  ) => void;
  removeIndividualContact: (contactId: number) => void;
  setTeamContacts: (contacts: InviteWholeTeamFormContact[]) => void;
  clearTeamContacts: () => void;
  updateMessage: (newMessage: string) => void;
}

export interface ManagedInviteFormPropsWithInitialData
  extends ManagedInviteFormProps {
  initialInviteFormData: InitialInviteFormData;
}

const useUniqueId = () => {
  const idCounter = useRef(0);
  const getId = useCallback(() => idCounter.current++, []);
  return { getId };
};

/**
 * Returns invite form control states and props that handles managing
 *   invitee data. Logic related to initialization and submission are
 *   handled in `useManagedInviteFormWithInitialData`
 */
export const useManagedInviteForm = ({
  initialMessage = "",
  initialEmptyInputCount = 0,
  emailValidator,
}: UseManagedInviteFormArgs): UseManagedInviteFormReturnType => {
  const { getId } = useUniqueId();

  const [message, setMessage] = useState<string>(initialMessage);
  const [teamContacts, setTeamContacts] = useState<
    Readonly<InviteWholeTeamFormContact[]>
  >([]);
  const [individualContacts, setIndividualContacts] = useState<
    Readonly<IndividualInviteFormContact[]>
  >(() =>
    Array.from({ length: Math.max(initialEmptyInputCount, 0) }, () => ({
      contactId: getId(),
      email: null,
      newRole: null,
    })),
  );

  // For consistency, we re-initialize form with fresh empty inputs on initialEmptyInputCount change,
  //   but only if the input does not already have a valid values.
  // In practice, the value of initialEmptyInputCount should only change during initialization and
  //   should not change after the form is visible and interactable
  useEffect(() => {
    setIndividualContacts((prev) => {
      const shouldReInitialize =
        prev.length !== initialEmptyInputCount && prev.every((c) => !c.email);
      return shouldReInitialize
        ? Array.from({ length: Math.max(initialEmptyInputCount, 0) }, () => ({
            contactId: getId(),
            email: null,
            newRole: null,
          }))
        : prev;
    });
  }, [initialEmptyInputCount, getId]);

  const individualContactsWithDuplicatesCheck = useMemo(() => {
    return markDuplicateContacts({
      individualContacts,
      teamContacts,
    });
  }, [individualContacts, teamContacts]);

  const validIndividualContactCount = useMemo(() => {
    return individualContactsWithDuplicatesCheck.filter(isValidInvitee).length;
  }, [individualContactsWithDuplicatesCheck]);

  const validTeamContactCount = useMemo(() => {
    return teamContacts.filter(isValidInvitee).length;
  }, [teamContacts]);

  const clearTeamContacts = useCallback(() => {
    setTeamContacts([]);
  }, []);

  const addEmptyIndividualContact = useCallback(() => {
    setIndividualContacts((prev) => [
      ...prev,
      { contactId: getId(), email: null, newRole: null },
    ]);
  }, [getId]);
  const updateIndividualContact = useCallback(
    async (contact: IndividualInviteFormContact) => {
      // Update first, then validate email if needed and update again
      setIndividualContacts((prev) =>
        prev.map((c) =>
          c.contactId === contact.contactId
            ? {
                ...contact,
                validationResult: EmailValidationResult.VALIDATION_PENDING,
              }
            : c,
        ),
      );

      const { email } = contact;
      const validationResult =
        email && emailValidator ? await emailValidator(email) : undefined;
      setIndividualContacts((prev) => {
        return prev.map((c) =>
          c.contactId === contact.contactId
            ? { ...c, ...contact, validationResult }
            : c,
        );
      });
    },
    [emailValidator],
  );

  const validateBatchContacts = useCallback(
    async (contacts: readonly IndividualInviteFormContact[]) => {
      setIndividualContacts((prev) => {
        return prev.map((c) => ({
          ...c,
          validationResult: EmailValidationResult.VALIDATION_PENDING,
        }));
      });
      const contactsMapWithResults = new Map<
        number,
        EmailValidationResult | null | undefined
      >();
      await Promise.all(
        contacts.map(async (contact) => {
          const { email } = contact;
          const validationResultPromise =
            !email || !emailValidator
              ? Promise.resolve(undefined)
              : emailValidator(email);
          return validationResultPromise.then((validationResult) =>
            contactsMapWithResults.set(contact.contactId, validationResult),
          );
        }),
      );
      contactsMapWithResults.forEach((validationResult, contactId) => {
        setIndividualContacts((prev) => {
          return prev.map((c) =>
            c.contactId === contactId ? { ...c, validationResult } : c,
          );
        });
      });
    },
    [emailValidator],
  );

  const removeIndividualContact = useCallback((contactId: number) => {
    setIndividualContacts((prev) =>
      prev.filter((contact) => contact.contactId !== contactId),
    );
  }, []);

  return {
    managedInviteFormProps: {
      individualContacts: individualContactsWithDuplicatesCheck,
      teamContacts,
      validIndividualContactCount,
      validTeamContactCount,
      initialMessage,
      customMessage: message,
      addEmptyIndividualContact,
      updateIndividualContact,
      validateBatchContacts,
      removeIndividualContact,
      setTeamContacts,
      clearTeamContacts,
      updateMessage: setMessage,
    },
  };
};

/**
 * Returns invite form control states and props
 */
export const useManagedInviteFormWithInitialData = ({
  initialMessage,
  defaultInitialEmptyInputCount = 4,
  dashInviteFormSource,
  onFormValidationChange,
}: UseManagedInviteFormWithInitialDataArgs): UseManagedInviteFormWithInitialDataReturnType => {
  const { status: initialInviteFormDataStatus, data: initialData } =
    useInitialInviteFormData();

  const inviterHasTeamMembers =
    (initialData?.teamMembers && initialData?.teamMembers.length > 0) ?? false;
  const canInviteAllTeamMembers = initialData?.canInviteAllTeamMembers ?? false;
  const canAssignRoles = initialData?.canAssignRoles ?? false;
  const canInviteWithOptionalMessage =
    initialData?.canInviteWithOptionalMessage ?? false;

  // Calculate the number of initial empty input fields to include in the invite form
  // If the invite form is being used in the onboarding flow, then maintain 4 initial empty fields
  // If the invite form is being used in the invite modal, then determine the number of empty fields by whether the invite-entire-team checkbox is shown

  const initialEmptyInputCount =
    dashInviteFormSource === DashInviteFormSource.DASH_HOMEPAGE_MODAL &&
    canInviteAllTeamMembers &&
    inviterHasTeamMembers
      ? INITIAL_EMPTY_INPUT_COUNT_MODAL_WITH_TEAM_CHECKBOX
      : defaultInitialEmptyInputCount;

  const { emailValidator, emailValidatorEmptyCache } =
    useInviteFormEmailValidation({
      knownValidTeamMemberInvitees:
        initialInviteFormDataStatus === INITIAL_INVITE_FORM_DATA_STATUS.READY
          ? initialData.teamMembers
          : [],
    });

  const { managedInviteFormProps } = useManagedInviteForm({
    initialMessage,
    initialEmptyInputCount,
    emailValidator,
  });

  const {
    customMessage,
    individualContacts,
    teamContacts,
    validIndividualContactCount,
    validTeamContactCount,
  } = managedInviteFormProps;

  const customMessageFiltered = canInviteWithOptionalMessage
    ? customMessage
    : "";

  const isValidForm = useMemo(() => {
    if (!initialData) {
      return false;
    }
    const NoValidContact =
      validIndividualContactCount + validTeamContactCount === 0;
    const OverQuota =
      validIndividualContactCount +
        validTeamContactCount +
        initialData.licensesUsed >
      initialData.licenseCount;
    // Unvalidated e-mail are seen as invalid. See notes above on isValidInvitee
    const HasNonEmptyInvalidInvitee = [
      ...individualContacts.filter((c) => !!c.email),
      ...teamContacts,
    ].some((c) => !isValidInvitee(c));
    const isValid = !NoValidContact && !OverQuota && !HasNonEmptyInvalidInvitee;
    return isValid;
  }, [
    initialData,
    validIndividualContactCount,
    validTeamContactCount,
    individualContacts,
    teamContacts,
  ]);

  const { handleSubmit, submissionStatus, submissionResult } =
    useSubmitInviteForm({
      canAssignRoles,
      customMessage: customMessageFiltered,
      dashInviteFormSource,
      individualContacts,
      teamContacts,
      isValidForm,
    });

  const inviteFormStatus: InviteFormStatus = useMemo(() => {
    if (initialInviteFormDataStatus === INITIAL_INVITE_FORM_DATA_STATUS.READY) {
      if (initialData?.licensesUsed >= initialData?.licenseCount) {
        return InviteFormStatus.OVER_QUOTA_LICENSE_LIMIT;
      } else if (submissionStatus === SUBMISSION_STATUS_SUBMITTED) {
        return InviteFormStatus.SUBMITTED;
      } else if (submissionStatus === SUBMISSION_STATUS_SUBMITTING) {
        emailValidatorEmptyCache();
        return InviteFormStatus.SUBMITTING;
      } else if (isValidForm) {
        return InviteFormStatus.CAN_SUBMIT;
      } else {
        return InviteFormStatus.CANNOT_SUBMIT;
      }
    } else {
      // We don't currently have an error state for the invite form, so just keep it as loading if there's an error
      return InviteFormStatus.LOADING;
    }
  }, [
    initialInviteFormDataStatus,
    submissionStatus,
    isValidForm,
    initialData?.licensesUsed,
    initialData?.licenseCount,
    emailValidatorEmptyCache,
  ]);

  useEffect(() => {
    onFormValidationChange?.(isValidForm);
  }, [isValidForm, onFormValidationChange]);

  // Build a new object containing fields we want to pass down to invite form
  // Overwrites canInviteAllTeamMembers based on original value of flag and whether the inviter has team members
  const initialDataFiltered: InitialInviteFormData | null = initialData
    ? {
        licenseCount: initialData.licenseCount,
        licensesUsed: initialData.licensesUsed,
        teamMembers: initialData.teamMembers,
        canInviteAllTeamMembers,
        canAssignRoles,
        canInviteWithOptionalMessage,
        isFssTeam: initialData.isFssTeam,
      }
    : null;

  return {
    inviteFormStatus,
    submissionResult,
    handleSubmit,
    managedInviteFormProps: initialDataFiltered
      ? {
          ...managedInviteFormProps,
          initialInviteFormData: initialDataFiltered,
          disabled: submissionStatus === SUBMISSION_STATUS_SUBMITTING,
        }
      : null,
  };
};

type markDuplicateContactsProps = {
  individualContacts: Readonly<IndividualInviteFormContact[]>;
  teamContacts: Readonly<InviteWholeTeamFormContact[]>;
};
export const markDuplicateContacts = ({
  individualContacts,
  teamContacts,
}: markDuplicateContactsProps): IndividualInviteFormContact[] => {
  const seenEmails = new Set(
    teamContacts
      .filter((c) => c.email !== null)
      .map((c) => c.email.toLowerCase().trim()),
  );

  return individualContacts.map((contact) => {
    const { email } = contact;
    if (!email) {
      return { ...contact };
    }
    const normalizedEmail = email.toLowerCase().trim();

    if (seenEmails.has(normalizedEmail)) {
      return {
        ...contact,
        validationResult: EmailValidationResult.DUPLICATE_EMAIL,
      };
    }
    seenEmails.add(normalizedEmail);
    return { ...contact };
  });
};
